import { Activity, HeartPulse, Droplets, Leaf, RefreshCw } from "lucide-react";
import { MANIFESTO, RISK_FACTORS, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";

const RISK_ICONS = [Activity, HeartPulse, Droplets, Leaf];

export default function PreventiveHeartCare() {
  return (
    <>
      <PageHero
        crumb="Preventive Care"
        eyebrow="Preventive Heart Care"
        titleLines={["Stay Ahead of", "Heart Disease —", "Varanasi"]}
        subtitle="Empowering you with the knowledge and strategy for a lifelong healthy heart."
      />

      {/* Importance */}
      <section className="pb-8 bg-paper" data-testid="preventive-importance">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">The Importance of Prevention</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink font-light text-balance">
                Heart disease is largely preventable.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <Reveal delay={0.1}>
              <p className="text-lg text-sage leading-relaxed">
                Proactive heart health means finding and reducing risk long before symptoms appear. Through structured risk assessment and screening, Dr. Mishra identifies what threatens your heart — and builds a plan to protect it for the decades ahead.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Manifesto chapters */}
      <section className="py-24 md:py-32 bg-surface border-y border-line mt-16" data-testid="preventive-manifesto">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-12">Our Preventive Philosophy</p>
          </Reveal>
          <div className="space-y-px bg-line border border-line rounded-3xl overflow-hidden">
            {MANIFESTO.map((m) => (
              <Reveal key={m.n}>
                <div className="group bg-paper grid grid-cols-1 md:grid-cols-12 gap-6 p-8 md:p-12 transition-colors duration-300 hover:bg-mint/40">
                  <div className="md:col-span-3">
                    <span className="font-display text-7xl md:text-8xl text-brand/25 leading-none transition-colors duration-300 group-hover:text-brand">{m.n}</span>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-display text-3xl md:text-4xl text-ink mb-3">{m.title}</h3>
                    <p className="text-base md:text-lg text-sage leading-relaxed max-w-2xl">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Managing risk factors */}
      <section className="py-24 md:py-32 bg-paper" data-testid="preventive-risks">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Managing Key Risk Factors</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light max-w-3xl mb-14">
              The levers that protect your heart.
            </h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {RISK_FACTORS.map((r, i) => {
              const RIcon = RISK_ICONS[i] || HeartPulse;
              return (
                <StaggerItem key={r.title}>
                  <div className="group rounded-3xl border border-line bg-surface p-8 h-full transition-colors duration-300 hover:border-brand">
                    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-mint text-brand mb-6 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <RIcon size={24} />
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl text-ink mb-3">{r.title}</h3>
                    <p className="text-sm md:text-base text-sage leading-relaxed">{r.body}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>

          <Reveal delay={0.1}>
            <div className="mt-10 flex items-start gap-4 rounded-3xl bg-mint border border-line p-8">
              <RefreshCw size={26} className="text-brand shrink-0 mt-1" />
              <div>
                <h4 className="font-display text-2xl text-ink mb-2">Regular Follow-up & Monitoring</h4>
                <p className="text-sage leading-relaxed max-w-3xl">Prevention is a relationship, not a single visit. Ongoing monitoring keeps your plan responsive to how your body and life change over time.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FAQSection items={FAQS.preventive} heading="Preventive Care — Common Questions" />
      <CTASection title="Start Your Preventive Heart Care Plan" />
    </>
  );
}
